import { reactive } from 'vue';
import { t } from './i18n';
import { logger } from './platform';
import { useApp } from './stores/app';

/**
 * The app as a PWA: the service worker (pwa/sw.js, written by the build) keeps the whole build on the
 * device, so the app opens and works without a network; installing puts it in its own window.
 * A new release installs in the background and waits: the user is offered to switch (open canvases
 * are written first), it never happens by itself in the middle of work.
 */
interface InstallPrompt extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const pwa = reactive({
  /** The browser offers installing (the menu shows "Install the app"). */
  installable: false,
  /** Running as the installed app (its own window). */
  standalone: typeof matchMedia !== 'undefined' && (matchMedia('(display-mode: standalone)').matches || matchMedia('(display-mode: window-controls-overlay)').matches),
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  /** The build is on the device: the app opens without a network. */
  offlineReady: false,
});

let deferred: InstallPrompt | null = null;
let reloading = false;

export function installPwa(): void {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as InstallPrompt;
    pwa.installable = true;
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    pwa.installable = false;
    logger.info('pwa.installed', {});
  });
  window.addEventListener('offline', () => {
    pwa.online = false;
    useApp().notify('info', t('pwa.offline'), 6000);
  });
  window.addEventListener('online', () => (pwa.online = true));

  if (__BUILD_ID__ === 'dev' || !('serviceWorker' in navigator) || !window.isSecureContext) {
    watchVersionFile();
    return;
  }
  void register();
}

async function register(): Promise<void> {
  let reg: ServiceWorkerRegistration;
  try {
    reg = await navigator.serviceWorker.register('sw.js', { scope: './' });
  } catch (e) {
    logger.warn('pwa.register', { error: e instanceof Error ? e.name : 'unknown' });
    watchVersionFile();
    return;
  }
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) window.location.reload();
  });
  const track = (w: ServiceWorker) => {
    const done = () => {
      if (w.state === 'installed' && navigator.serviceWorker.controller) offerUpdate(w);
      // quietly: the state is shown in Settings → About
      if (w.state === 'activated') pwa.offlineReady = true;
    };
    w.addEventListener('statechange', done);
    done();
  };
  if (reg.active) pwa.offlineReady = true;
  if (reg.waiting && navigator.serviceWorker.controller) offerUpdate(reg.waiting);
  if (reg.installing) track(reg.installing);
  reg.addEventListener('updatefound', () => reg.installing && track(reg.installing));
  // look for a new release now and then, and when the tab comes back
  const check = () => {
    if (document.visibilityState === 'visible' && navigator.onLine) void reg.update().catch(() => undefined);
  };
  setInterval(check, 30 * 60 * 1000);
  document.addEventListener('visibilitychange', check);
}

let offered = false;
function offerUpdate(w: ServiceWorker): void {
  if (offered) return;
  offered = true;
  useApp().notify('info', t('app.updateAvailable'), 120000, [
    {
      label: t('pwa.updateNow'),
      run: () => void applyUpdate(w),
    },
  ]);
}

async function applyUpdate(w: ServiceWorker): Promise<void> {
  for (const s of useApp().sessions.values()) await s.flush().catch(() => undefined);
  reloading = true;
  w.postMessage({ type: 'skip-waiting' });
}

/** The browser's install offer (from the menu). */
export async function promptInstall(): Promise<void> {
  if (!deferred) return;
  await deferred.prompt();
  const { outcome } = await deferred.userChoice;
  if (outcome === 'accepted') pwa.installable = false;
  deferred = null;
}

/** Without a service worker (development, an insecure address): compare version.json with this build. */
function watchVersionFile(): void {
  if (__BUILD_ID__ === 'dev') return;
  let notified = false;
  const check = async () => {
    if (notified || document.visibilityState !== 'visible') return;
    try {
      const res = await fetch('version.json', { cache: 'no-store' });
      if (!res.ok) return;
      const v = (await res.json()) as { build?: string };
      if (v.build && v.build !== __BUILD_ID__) {
        notified = true;
        useApp().notify('info', t('app.updateAvailable'), 60000);
      }
    } catch {
      /* offline */
    }
  };
  setInterval(check, 10 * 60 * 1000);
  document.addEventListener('visibilitychange', check);
}
